import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Anchor, Box, Button, Grid, Heading, Menu, ResponsiveContext, Stack, Tab, Tabs, Text, TextInput } from "grommet";
import * as React from "react";
import Avatar from "react-avatar";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { usePermission, useUser } from "../account/AccountStore";
import CustomButton from "../components/CustomButton";
import LinkAnchor from "../components/LinkAnchor";
import RichTabTitle from "../components/RichTabTitle";
import { ResolveUsers } from "../organizations/IdentityAPI";
import { AliasCard } from "./AliasCard";
import { AliasViewModel, searchAliases } from "./AliasStore";
import { DeleteAliasButton } from "./DeleteAlias";
import { AliasGrid } from "./AliasGrid";
import { AliasRow } from "./AliasRow";
import Theme from "../utils/Theme";

//@ts-ignore
const plus = <FontAwesomeIcon size="xl" icon={["fad", "user-plus"]}></FontAwesomeIcon>;
const edit = <FontAwesomeIcon icon={["fad", "pencil"]} size="sm"></FontAwesomeIcon>;
const idBadge = <FontAwesomeIcon icon={["fad", "id-badge"]} size="sm"></FontAwesomeIcon>;
const search = <FontAwesomeIcon icon={["fad", "search"]}></FontAwesomeIcon>;

const AliasTable = () => {
    const user = useUser();

    let { pathname } = useLocation();
    const navigate = useNavigate();

    const [aliases, setAliases] = React.useState([] as AliasViewModel[]);
    const [filter, setFilter] = React.useState("");
    const [filteredAliases, setFilteredAliases] = React.useState([] as AliasViewModel[]);

    const [includeArchived, setIncludeArchived] = React.useState(false);

    const getAliases = () => {
        searchAliases("", user.organization.id, 0, 200, includeArchived).then((aliases) => {
            ResolveUsers(aliases.map((alias) => alias.identityUserID).filter((a) => a)).then((users) => {
                setAliases(aliases.map((a) => ({ ...a, identityUser: users.find((u) => u.id == a.identityUserID) } as AliasViewModel)));
            });
        });
    };

    React.useEffect(() => {
        getAliases();
    }, [pathname, user, includeArchived]);

    React.useEffect(() => {
        if (filter) {
            setFilteredAliases(aliases.filter((t) => t.name.toLowerCase().indexOf(filter.toLowerCase()) != -1));
        } else {
            setFilteredAliases(aliases);
        }
    }, [aliases, filter]);

    const size = React.useContext(ResponsiveContext);

    const [index, setIndex] = React.useState(0);
    const onActive = (nextIndex: number) => setIndex(nextIndex);

    const exportAliases = () => {
        var aoa = filteredAliases.map((alias) => [alias.name, alias.identityUser?.displayName || ""]);

        aoa = [["Alias", "Name"]].concat(aoa);

        var ws = XLSX.utils.aoa_to_sheet(aoa);
        var wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Data");

        XLSX.writeFile(wb, "export.xlsx");
    };

    const hasWritePermission = usePermission("sport:aliases.write");

    return (
        <div>
            {/* <Heading size="small" textAlign="center" fill>
                Aliases
            </Heading> */}
            <Box direction="row" gap="small" justify="between">
                {hasWritePermission && (
                    <Box direction="row" gap="small">
                        <CustomButton path={"/aliases/create"} icon={plus} label="Add Player"></CustomButton>
                    </Box>
                )}
                <Box direction="row" justify="end" gap="small" align="center">
                    <Box pad="small">
                        <TextInput icon={search} placeholder="Search..." value={filter} onChange={(e) => setFilter(e.target.value)}></TextInput>
                    </Box>
                    <Box pad="small" hoverIndicator="background-back" onClick={() => setIncludeArchived(!includeArchived)} round="5px">
                        <FontAwesomeIcon size="lg" icon={["fad", "archive"]} color={includeArchived ? "#c27b00" : ""} />
                    </Box>
                    <Box pad="small" hoverIndicator="background-back" onClick={() => onActive(0)} round="5px">
                        <FontAwesomeIcon size="lg" icon={["fad", "grid-2"]} color={index == 0 ? "var(--accent)" : ""} />
                    </Box>
                    <Box pad="small" hoverIndicator="background-back" onClick={() => onActive(1)} round="5px">
                        <FontAwesomeIcon size="lg" icon={["fad", "list"]} color={index == 1 ? "var(--accent)" : ""} />
                    </Box>
                </Box>
            </Box>
            <Tabs activeIndex={index} onActive={onActive}>
                <Tab>
                    <AliasGrid aliases={filteredAliases}></AliasGrid>
                </Tab>
                <Tab>
                    <Box gap="small">
                        {filteredAliases.map((alias) => (
                            <AliasRow alias={alias} key={alias.id}>
                                <Box direction="row" gap="small">
                                    {size == "small" && (
                                        <Menu
                                            items={[
                                                {
                                                    label: "Profile",
                                                    onClick: () => {
                                                        navigate("/aliases/" + alias.id);
                                                    },
                                                },
                                                {
                                                    label: "Home Page",
                                                    onClick: () => {
                                                        navigate("/ue/aliases/" + alias.id);
                                                    },
                                                },
                                            ]}
                                        />
                                    )}
                                    {size != "small" && (
                                        <Box direction="row" gap="small" align="center">
                                            {alias.archived && (
                                                <Box background="status-warning" pad={"xxsmall"} round="3px">
                                                    ARCHIVED
                                                </Box>
                                            )}
                                            <CustomButton primary path={"/aliases/" + alias.id} icon={edit} label="Profile" size="small"></CustomButton>
                                            <CustomButton primary href={"/ue/aliases/" + alias.id} icon={idBadge} label="Home Page" size="small"></CustomButton>
                                            {user.hasPermission("sport:aliases.delete") && <DeleteAliasButton alias={alias} size="small" onDelete={() => getAliases()}></DeleteAliasButton>}
                                        </Box>
                                    )}
                                </Box>
                            </AliasRow>
                        ))}
                    </Box>
                </Tab>
            </Tabs>
            <Box pad={{ top: "small" }}>
                {aliases.length != filteredAliases.length && (
                    <Text>
                        Showing {filteredAliases.length} of {aliases.length}{" "}
                        <Anchor href="#" onClick={() => setFilter("")}>
                            Show All
                        </Anchor>
                    </Text>
                )}
            </Box>
        </div>
    );
};

export default AliasTable;
